import { Money } from "../../../../../../lib/Money";
import type {
    SalesInvoiceRow,
    SalesInvoiceSummaryItem,
    SalesInvoiceTabId,
} from "../types/salesInvoice";
import type {CurrencyEnum, SalesInvoiceDisplayStatus} from '../../../../../../generated/enums.ts';

export function formatDateDDMMYYYY(isoDate: string | null): string {
    if (!isoDate) return "—";
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return "—";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
}

export function formatDateTimeDDMMYYYY(isoDate: string | null): string {
    if (!isoDate) return "—";
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return "—";
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${formatDateDDMMYYYY(isoDate)} · ${hours}:${minutes}`;
}

export type StatusMeta = {
    label: string;
    pillClasses: string;
    dotClasses: string;
};

export const STATUS_META: Record<SalesInvoiceDisplayStatus, StatusMeta> = {
    draft: {
        label: "Concept",
        pillClasses: "bg-gray-100 text-gray-700",
        dotClasses: "bg-gray-400",
    },
    outstanding: {
        label: "Openstaand",
        pillClasses: "bg-blue-50 text-blue-700",
        dotClasses: "bg-blue-500",
    },
    overdue: {
        label: "Te laat",
        pillClasses: "bg-red-50 text-red-700",
        dotClasses: "bg-red-500",
    },
    paid: {
        label: "Betaald",
        pillClasses: "bg-green-50 text-green-700",
        dotClasses: "bg-green-500",
    },
    credit_note: {
        label: "Credit",
        pillClasses: "bg-gray-100 text-gray-700",
        dotClasses: "bg-gray-400",
    },
};

export const STATUS_SORT_ORDER: SalesInvoiceDisplayStatus[] = [
    "overdue",
    "outstanding",
    "paid",
    "credit_note",
    "draft",
];

export function statusSortIndex(status: SalesInvoiceDisplayStatus): number {
    const index = STATUS_SORT_ORDER.indexOf(status);
    return index === -1 ? STATUS_SORT_ORDER.length : index;
}

export const TAB_ORDER: SalesInvoiceTabId[] = [
    "alle",
    "outstanding",
    "overdue",
    "paid",
    "credit_note",
];

export const TAB_LABELS: Record<SalesInvoiceTabId, string> = {
    alle: "Alle",
    outstanding: "Openstaand",
    overdue: "Te laat",
    paid: "Betaald",
    credit_note: "Credit",
};

export const EMPTY_MESSAGES: Record<SalesInvoiceTabId, string> = {
    alle: "Nog geen facturen.",
    outstanding: "Geen openstaande facturen.",
    overdue: "Geen facturen over de vervaldatum.",
    paid: "Geen betaalde facturen.",
    credit_note: "Geen creditfacturen.",
};

export function filterInvoicesByTab(
    invoices: SalesInvoiceRow[],
    tab: SalesInvoiceTabId
): SalesInvoiceRow[] {
    if (tab === "alle") return invoices;
    return invoices.filter((invoice) => invoice.status === tab);
}

export function computeTabCounts(
    invoices: SalesInvoiceRow[]
): Record<SalesInvoiceTabId, number> {
    const counts: Record<SalesInvoiceTabId, number> = {
        alle: invoices.length,
        outstanding: 0,
        overdue: 0,
        paid: 0,
        credit_note: 0,
    };
    for (const invoice of invoices) {
        if (invoice.status !== "draft") {
            counts[invoice.status] += 1;
        }
    }
    return counts;
}

export function summaryBucketForCurrency(
    items: SalesInvoiceSummaryItem[] | undefined,
    currency: CurrencyEnum
): { value: Money; currency: CurrencyEnum } {
    const item = items?.find((entry) => entry.currency === currency);
    if (!item) {
        return { value: { signed_amount: 0, currency }, currency };
    }
    return { value: item.total_amount, currency };
}
